import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  KeyIcon,
  CalendarIcon,
  UserGroupIcon,
  DocumentIcon,
  CircleStackIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  PhoneIcon,
  EnvelopeIcon,
  ChatBubbleLeftRightIcon,
  GlobeAltIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';
import { useAuthStore } from '../store/authStore';
import api from '../services/api';

const LicenseInfoPage = () => {
  const [license, setLicense] = useState(null);
  const [checkData, setCheckData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const user = useAuthStore((state) => state.user);

  useEffect(() => {
    fetchLicense();
  }, []);

  const fetchLicense = async () => {
    try {
      setLoading(true);
      setError(null);

      // Step 1: Check license validity
      const response = await api.get('/license/check');
      setCheckData(response.data);

      if (!response.data.valid) {
        setError(response.data.message || 'No valid license found');
        return;
      }

      // Step 2: Try to get full license details
      if (user?.tenant_id) {
        try {
          const detailResponse = await api.get(`/license/tenant/${user.tenant_id}`);
          setLicense(detailResponse.data);
        } catch {
          // If detail fetch fails (e.g. permissions), use check data + user data as fallback
        }
      }
    } catch (err) {
      console.error('Error fetching license:', err);
      setError(err.response?.data?.detail || 'Failed to fetch license information');
    } finally {
      setLoading(false);
    }
  };

  const getStatusChip = () => {
    const daysRemaining = license?.days_remaining ?? checkData?.days_remaining;
    const isActive = license?.is_active ?? checkData?.valid;

    if (!isActive) {
      return (
        <Chip
          icon={<XCircleIcon className="h-4 w-4" />}
          label="Inactive"
          color="error"
          sx={{ fontSize: '1rem', py: 2, px: 1 }}
        />
      );
    }

    if (daysRemaining != null && daysRemaining <= 0) {
      return (
        <Chip
          icon={<XCircleIcon className="h-4 w-4" />}
          label="Expired"
          color="error"
          sx={{ fontSize: '1rem', py: 2, px: 1 }}
        />
      );
    }

    if (daysRemaining != null && daysRemaining <= 30) {
      return (
        <Chip
          icon={<ExclamationTriangleIcon className="h-4 w-4" />}
          label={`Expiring Soon (${daysRemaining} days)`}
          color="warning"
          sx={{ fontSize: '1rem', py: 2, px: 1 }}
        />
      );
    }

    return (
      <Chip
        icon={<CheckCircleIcon className="h-4 w-4" />}
        label="Active"
        color="success"
        sx={{ fontSize: '1rem', py: 2, px: 1 }}
      />
    );
  };

  const UsageCard = ({ icon: Icon, title, value, color = 'primary' }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Icon className="h-6 w-6 mr-2" style={{ color: color === 'primary' ? '#1976d2' : color }} />
          <Typography variant="subtitle1" color="text.secondary">
            {title}
          </Typography>
        </Box>
        <Typography variant="h4" fontWeight="bold">
          {value}
        </Typography>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <XCircleIcon className="h-16 w-16 mx-auto mb-4 text-red-500" />
          <Typography variant="h5" gutterBottom>
            No License Found
          </Typography>
          <Typography color="text.secondary">
            Please contact your administrator to obtain a valid license for your organization.
          </Typography>
        </Paper>
      </Container>
    );
  }

  // Use full license details if available, otherwise fall back to check data + user data
  const daysRemaining = license?.days_remaining ?? checkData?.days_remaining ?? user?.license_days_remaining;
  const companyName = license?.company_name ?? user?.tenant_name ?? 'Your Organization';
  const licenseKey = license?.license_key ?? checkData?.license_key;
  const expiryDate = license?.expiry_date ?? checkData?.expiry_date;
  const issueDate = license?.issue_date;
  const tier = user?.subscription_tier;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          License Information
        </Typography>
        <Typography variant="body1" color="text.secondary">
          View your organization's license details and usage limits
        </Typography>
      </Box>

      {/* License Status Card */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <KeyIcon className="h-12 w-12 text-blue-600" />
              <Box>
                <Typography variant="h5" fontWeight="bold">
                  {companyName}
                </Typography>
                {tier && (
                  <Chip
                    label={tier.charAt(0).toUpperCase() + tier.slice(1)}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ mt: 0.5 }}
                  />
                )}
                {licenseKey && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', mt: 0.5 }}>
                    {licenseKey}
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, alignItems: 'center', gap: 2 }}>
              {getStatusChip()}
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* License Dates */}
        <Grid container spacing={3}>
          {issueDate && (
            <Grid item xs={12} sm={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarIcon className="h-5 w-5 text-gray-500" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Issue Date
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {new Date(issueDate).toLocaleDateString()}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}
          <Grid item xs={12} sm={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarIcon className="h-5 w-5 text-gray-500" />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Expiry Date
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {expiryDate ? new Date(expiryDate).toLocaleDateString() : '-'}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarIcon className="h-5 w-5 text-gray-500" />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Days Remaining
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight="medium"
                  color={daysRemaining != null && daysRemaining <= 30 ? 'error.main' : 'text.primary'}
                >
                  {daysRemaining ?? '-'} days
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Days remaining progress bar */}
        {daysRemaining != null && daysRemaining > 0 && (
          <Box sx={{ mt: 3 }}>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, (daysRemaining / 365) * 100)}
              color={daysRemaining > 30 ? 'success' : daysRemaining > 7 ? 'warning' : 'error'}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>
        )}
      </Paper>

      {/* Usage Limits - only show if full license details available */}
      {license && (
        <>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            License Limits
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <UsageCard
                icon={ChatBubbleLeftRightIcon}
                title="AI answers"
                value={license.max_ai_answers ? `${license.max_ai_answers.toLocaleString()}/mo` : '-'}
                color="#6366f1"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <UsageCard
                icon={GlobeAltIcon}
                title="Websites"
                value={license.max_websites?.toLocaleString() ?? '-'}
                color="#0891b2"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <UsageCard
                icon={DocumentDuplicateIcon}
                title="Web Pages"
                value={license.max_scrape_pages?.toLocaleString() ?? '-'}
                color="#0d9488"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <UsageCard
                icon={UserGroupIcon}
                title="Users"
                value={license.max_users?.toLocaleString() ?? '-'}
                color="#1976d2"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <UsageCard
                icon={PhoneIcon}
                title="WhatsApp Numbers"
                value={license.included_notification_phones?.toLocaleString() ?? '-'}
                color="#25D366"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <UsageCard
                icon={EnvelopeIcon}
                title="Email Notifications"
                value={license.included_notification_emails?.toLocaleString() ?? '-'}
                color="#7c3aed"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <UsageCard
                icon={CircleStackIcon}
                title="Knowledge pages"
                value={license.max_knowledge_pages ? license.max_knowledge_pages.toLocaleString() : '-'}
                color="#ea580c"
              />
            </Grid>
          </Grid>
        </>
      )}

      {/* Warning if expiring soon */}
      {daysRemaining != null && daysRemaining <= 30 && daysRemaining > 0 && (
        <Alert severity="warning" sx={{ mt: 4 }}>
          <Typography fontWeight="bold">License Expiring Soon</Typography>
          <Typography variant="body2">
            Your license will expire in {daysRemaining} days. Please contact your administrator
            to renew your license before it expires to avoid service interruption.
          </Typography>
        </Alert>
      )}

      {/* Error if expired */}
      {daysRemaining != null && daysRemaining <= 0 && (
        <Alert severity="error" sx={{ mt: 4 }}>
          <Typography fontWeight="bold">License Expired</Typography>
          <Typography variant="body2">
            Your license has expired. Please contact your administrator immediately to renew your license.
          </Typography>
        </Alert>
      )}
    </Container>
  );
};

export default LicenseInfoPage;
